import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Save, Users, FileText, Bell, Link2, Calendar, Mail, Plus, Trash2, Send, RefreshCw, Palette, Check } from 'lucide-react';
import CustomFieldsEditor from '@/components/CustomFieldsEditor';
import { useBranding } from '@/contexts/BrandingContext';
import { PRESET_PALETTES, hexToRgb } from '@/lib/theme';

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadSettings = async () => {
    try { const res = await api.get('/settings'); setSettings(res.data); }
    catch { toast.error('Failed to load settings'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadSettings(); }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/settings', settings);
      toast.success('Settings saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const updateNested = (path, value) => {
    const updated = { ...settings };
    const keys = path.split('.');
    let obj = updated;
    for (let i = 0; i < keys.length - 1; i++) {
      obj[keys[i]] = { ...obj[keys[i]] };
      obj = obj[keys[i]];
    }
    obj[keys[keys.length - 1]] = value;
    setSettings(updated);
  };

  if (loading || !settings) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[#3A8C8C] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="settings-page">
      <div className="section-heading-row flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-['Manrope'] font-bold">Settings</h1>
          <p className="text-sm text-[#6B7280] mt-0.5">Studio configuration and preferences</p>
        </div>
        <button onClick={saveSettings} disabled={saving} data-testid="settings-save-btn"
          className="bg-[#3A8C8C] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#2E7A7A] transition-colors flex items-center gap-2 active:scale-95 disabled:opacity-50">
          <Save size={16} strokeWidth={1.5} /> {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <Tabs defaultValue="rates" className="w-full">
        <TabsList className="bg-[#E8E6E1]/50 rounded-xl p-1">
          <TabsTrigger value="rates" className="rounded-lg text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm">Default Rates</TabsTrigger>
          <TabsTrigger value="notifications" className="rounded-lg text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm"><Bell size={13} className="mr-1" /> Notifications</TabsTrigger>
          <TabsTrigger value="integrations" className="rounded-lg text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm"><Link2 size={13} className="mr-1" /> Integrations</TabsTrigger>
          <TabsTrigger value="custom-fields" className="rounded-lg text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm"><Plus size={13} className="mr-1" /> Custom Fields</TabsTrigger>
          <TabsTrigger value="documents" className="rounded-lg text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm"><FileText size={13} className="mr-1" /> Documents</TabsTrigger>
          <TabsTrigger value="appearance" className="rounded-lg text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm"><Palette size={13} className="mr-1" /> Appearance</TabsTrigger>
        </TabsList>

        {/* Rates */}
        <TabsContent value="rates" className="mt-6">
          <div className="glass-card rounded-2xl p-6">
            <h3 className="section-heading text-sm font-semibold font-['Manrope'] mb-4">Default Rates</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs text-[#6B7280] uppercase">Design Fee per Sqft (Rs)</Label>
                <Input type="number" value={settings.default_rates?.design_fee_per_sqft || 0}
                  onChange={e => updateNested('default_rates.design_fee_per_sqft', Number(e.target.value))}
                  className="mt-1 h-10 rounded-xl border-[#D4D0C8]" data-testid="rate-design-fee" />
              </div>
              <div>
                <Label className="text-xs text-[#6B7280] uppercase">Supervision Fee (%)</Label>
                <Input type="number" value={settings.default_rates?.supervision_fee_percent || 0}
                  onChange={e => updateNested('default_rates.supervision_fee_percent', Number(e.target.value))}
                  className="mt-1 h-10 rounded-xl border-[#D4D0C8]" data-testid="rate-supervision" />
              </div>
              <div>
                <Label className="text-xs text-[#6B7280] uppercase">GST Rate (%)</Label>
                <Input type="number" value={settings.default_rates?.gst_rate || 0}
                  onChange={e => updateNested('default_rates.gst_rate', Number(e.target.value))}
                  className="mt-1 h-10 rounded-xl border-[#D4D0C8]" data-testid="rate-gst" />
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Notifications */}
        <TabsContent value="notifications" className="mt-6">
          <div className="glass-card rounded-2xl p-6">
            <h3 className="section-heading text-sm font-semibold font-['Manrope'] mb-4">Notification Preferences</h3>
            <div className="space-y-3">
              {Object.entries(settings.notification_preferences || {}).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between py-2 border-b border-[#E8E6E1] last:border-0">
                  <span className="text-sm text-[#2C2C2E] capitalize">{key.replace(/_/g, ' ')}</span>
                  <Switch checked={value} onCheckedChange={v => updateNested(`notification_preferences.${key}`, v)} data-testid={`notif-${key}`} />
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Integrations */}
        <TabsContent value="integrations" className="mt-6 space-y-6">
          <div className="glass-card rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <Calendar size={16} strokeWidth={1.5} className="text-[#3A8C8C]" />
              <h3 className="section-heading text-sm font-semibold font-['Manrope']">Google Calendar</h3>
              <Badge className={settings.integrations?.google_calendar?.connected ? 'bg-[#7DA68A]/10 text-[#7DA68A] text-[10px]' : 'bg-[#C67D5B]/10 text-[#C67D5B] text-[10px]'}>
                {settings.integrations?.google_calendar?.connected ? 'Connected' : 'Not Connected'}
              </Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label className="text-xs text-[#6B7280] uppercase">Client ID</Label>
                <Input value={settings.integrations?.google_calendar?.client_id || ''} onChange={e => updateNested('integrations.google_calendar.client_id', e.target.value)} className="mt-1 h-9 rounded-lg border-[#D4D0C8] text-sm" placeholder="Google Cloud Console" data-testid="gcal-client-id" /></div>
              <div><Label className="text-xs text-[#6B7280] uppercase">Client Secret</Label>
                <Input type="password" value={settings.integrations?.google_calendar?.client_secret || ''} onChange={e => updateNested('integrations.google_calendar.client_secret', e.target.value)} className="mt-1 h-9 rounded-lg border-[#D4D0C8] text-sm" placeholder="OAuth client secret" data-testid="gcal-client-secret" /></div>
            </div>
          </div>

          <div className="glass-card rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <Mail size={16} strokeWidth={1.5} className="text-[#3A8C8C]" />
              <h3 className="section-heading text-sm font-semibold font-['Manrope']">Email</h3>
              <Badge className={settings.integrations?.email?.connected ? 'bg-[#7DA68A]/10 text-[#7DA68A] text-[10px]' : 'bg-[#C67D5B]/10 text-[#C67D5B] text-[10px]'}>
                {settings.integrations?.email?.connected ? 'Connected' : 'Not Connected'}
              </Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label className="text-xs text-[#6B7280] uppercase">Email Address</Label>
                <Input value={settings.integrations?.email?.email || ''} onChange={e => updateNested('integrations.email.email', e.target.value)} className="mt-1 h-9 rounded-lg border-[#D4D0C8] text-sm" placeholder="studio@interiorstory.com" /></div>
              <div><Label className="text-xs text-[#6B7280] uppercase">Provider</Label>
                <Input value={settings.integrations?.email?.provider || ''} onChange={e => updateNested('integrations.email.provider', e.target.value)} className="mt-1 h-9 rounded-lg border-[#D4D0C8] text-sm" placeholder="Gmail / Outlook" /></div>
            </div>
          </div>

          <TelegramCard />
        </TabsContent>

        {/* Custom Fields */}
        <TabsContent value="custom-fields" className="mt-6">
          <div className="glass-card rounded-2xl p-6">
            <h3 className="section-heading text-sm font-semibold font-['Manrope'] mb-4">Custom Fields</h3>
            <p className="text-xs text-[#6B7280] mb-4">Define custom fields that appear on leads, projects, tasks, and team member records.</p>
            <CustomFieldsEditor />
          </div>
        </TabsContent>

        {/* Documents — bank details, letterhead, reusable proposal template */}
        <TabsContent value="documents" className="mt-6 space-y-6">
          {/* Bank details */}
          <div className="glass-card rounded-2xl p-6">
            <h3 className="section-heading text-sm font-semibold font-['Manrope']">Bank Details</h3>
            <p className="text-xs text-[#6B7280] mb-4 mt-2">Added automatically to a proposal once the lead is Won, and to every invoice.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[['account_holder', 'Account Holder'], ['account_number', 'Account Number'], ['ifsc', 'IFSC'], ['branch', 'Branch'], ['account_type', 'Account Type']].map(([k, label]) => (
                <div key={k}>
                  <Label className="text-[10px] text-[#6B7280] uppercase">{label}</Label>
                  <Input value={settings.bank_details?.[k] || ''} onChange={e => updateNested(`bank_details.${k}`, e.target.value)}
                    className="mt-1 h-9 rounded-lg border-[#D4D0C8] text-sm" data-testid={`bank-${k}`} />
                </div>
              ))}
            </div>
          </div>

          {/* Letterhead contact */}
          <div className="glass-card rounded-2xl p-6">
            <h3 className="section-heading text-sm font-semibold font-['Manrope']">Letterhead Contact</h3>
            <p className="text-xs text-[#6B7280] mb-4 mt-2">Shown in the sidebar/footer of proposal &amp; invoice PDFs. The logo comes from Appearance.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[['tagline', 'Tagline'], ['email', 'Email'], ['phone', 'Phone'], ['website', 'Website']].map(([k, label]) => (
                <div key={k}>
                  <Label className="text-[10px] text-[#6B7280] uppercase">{label}</Label>
                  <Input value={settings.letterhead?.[k] || ''} onChange={e => updateNested(`letterhead.${k}`, e.target.value)}
                    className="mt-1 h-9 rounded-lg border-[#D4D0C8] text-sm" data-testid={`letterhead-${k}`} />
                </div>
              ))}
            </div>
          </div>

          {/* Proposal & invoice templates now live in Databases → Proposal & Invoice */}
          <div className="glass-card rounded-2xl p-6">
            <h3 className="section-heading text-sm font-semibold font-['Manrope']">Proposal &amp; Invoice Templates</h3>
            <p className="text-xs text-[#6B7280] mt-2">
              These have moved to <span className="font-medium text-[#2C2C2E]">Databases → Proposal &amp; Invoice</span>, alongside your other reusable data.
            </p>
            <a href="/databases" className="inline-flex items-center gap-1.5 mt-3 text-xs font-medium text-[#3A8C8C] hover:underline" data-testid="goto-doc-templates">
              <FileText size={13} strokeWidth={1.5} /> Open document templates
            </a>
          </div>
        </TabsContent>

        {/* Appearance */}
        <TabsContent value="appearance" className="mt-6">
          <AppearanceCard />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AppearanceCard() {
  const { branding, canManage, saveBranding, previewTheme, resetPreview } = useBranding();
  const [selected, setSelected] = useState(branding.theme?.primary || '#3A8C8C');
  const [custom, setCustom] = useState(branding.theme?.primary || '#3A8C8C');
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState(branding.accountName || '');
  const [savingBrand, setSavingBrand] = useState(false);
  const logoRef = useRef(null);

  useEffect(() => { setName(branding.accountName || ''); }, [branding.accountName]);

  const saveName = async () => {
    const n = name.trim();
    if (!n || n === branding.accountName) return;
    setSavingBrand(true);
    try { await saveBranding({ accountName: n }); toast.success('Studio name updated'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
    finally { setSavingBrand(false); }
  };
  const pickLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('Logo must be under 2 MB'); return; }
    setSavingBrand(true);
    try {
      const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
      await saveBranding({ logo_data_url: dataUrl });
      toast.success('Logo updated');
    } catch { toast.error('Logo upload failed'); }
    finally { setSavingBrand(false); }
  };
  const removeLogo = async () => {
    setSavingBrand(true);
    try { await saveBranding({ logo_data_url: '' }); toast.success('Logo removed'); }
    catch { toast.error('Failed'); }
    finally { setSavingBrand(false); }
  };

  useEffect(() => {
    const p = branding.theme?.primary || '#3A8C8C';
    setSelected(p); setCustom(p);
  }, [branding.theme?.primary]);

  // Live-preview the chosen color; revert to the saved theme on unmount.
  useEffect(() => {
    previewTheme({ primary: selected });
    return () => resetPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  if (!canManage) {
    return (
      <div className="glass-card rounded-2xl p-6" data-testid="appearance-card">
        <p className="text-sm text-[#6B7280]">Appearance and branding are managed by an administrator.</p>
      </div>
    );
  }

  const isValid = /^#[0-9a-fA-F]{6}$/.test(selected);
  const save = async () => {
    if (!isValid) { toast.error('Enter a valid hex color'); return; }
    setSaving(true);
    try { await saveBranding({ theme: { ...branding.theme, primary: selected } }); toast.success('Theme applied for everyone'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const logoUrl = branding.logoUrl;

  return (
    <div className="glass-card rounded-2xl p-6 space-y-6" data-testid="appearance-card">
      {/* Studio branding — name + logo */}
      <div>
        <h3 className="section-heading text-sm font-semibold font-['Manrope']">Studio Branding</h3>
        <p className="text-xs text-[#6B7280] mt-0.5">Your studio name and logo appear in the sidebar for everyone.</p>
        <div className="flex items-end gap-4 mt-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center overflow-hidden shrink-0 ${logoUrl ? 'bg-white border border-[#E8E6E1] p-1' : 'bg-[#3A8C8C]'}`}>
              {logoUrl
                ? <img src={logoUrl} alt="logo" className="max-w-full max-h-full object-contain" />
                : <span className="text-white font-bold font-['Manrope'] text-lg">{(name || 'IS').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}</span>}
            </div>
            <div className="flex flex-col gap-1">
              <button onClick={() => logoRef.current?.click()} disabled={savingBrand} data-testid="branding-logo-upload"
                className="text-xs text-[#3A8C8C] font-medium hover:underline text-left disabled:opacity-50">Upload logo</button>
              {logoUrl && <button onClick={removeLogo} disabled={savingBrand} className="text-xs text-[#C4727F] hover:underline text-left">Remove</button>}
              <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={pickLogo} />
            </div>
          </div>
          <div className="flex-1 min-w-[200px]">
            <Label className="text-[10px] text-[#6B7280] uppercase">Studio Name</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Interior Story"
                className="h-10 rounded-lg border-[#D4D0C8]" data-testid="branding-name-input" />
              <button onClick={saveName} disabled={savingBrand || !name.trim() || name.trim() === branding.accountName}
                className="h-10 px-4 rounded-lg bg-[#3A8C8C] text-white text-sm font-medium hover:bg-[#2E7A7A] disabled:opacity-40 shrink-0" data-testid="branding-name-save">Save</button>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-[#E8E6E1]" />

      <div>
        <h3 className="section-heading text-sm font-semibold font-['Manrope']">Color Palette</h3>
        <p className="text-xs text-[#6B7280] mt-0.5">Pick the studio's primary color. It recolors the sidebar, buttons and accents across the app for everyone. Changes preview live.</p>
      </div>

      <div className="grid grid-cols-4 sm:grid-cols-8 gap-3" data-testid="palette-presets">
        {PRESET_PALETTES.map(p => {
          const active = selected.toLowerCase() === p.primary.toLowerCase();
          return (
            <button key={p.id} onClick={() => { setSelected(p.primary); setCustom(p.primary); }}
              title={p.name} data-testid={`palette-${p.id}`}
              className={`h-12 rounded-xl flex items-center justify-center transition-all ${active ? 'ring-2 ring-offset-2 ring-[#2C2C2E]/60 scale-105' : 'hover:scale-105'}`}
              style={{ background: p.primary }}>
              {active && <Check size={16} className="text-white" strokeWidth={3} />}
            </button>
          );
        })}
      </div>

      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <Label className="text-[10px] text-[#6B7280] uppercase">Custom color</Label>
          <div className="flex items-center gap-2 mt-1">
            <input type="color" value={isValid ? selected : '#3A8C8C'} onChange={e => { setSelected(e.target.value); setCustom(e.target.value); }}
              className="w-10 h-10 rounded-lg border border-[#D4D0C8] bg-white cursor-pointer p-0.5" data-testid="palette-color-input" />
            <input value={custom} onChange={e => { const v = e.target.value; setCustom(v); if (/^#[0-9a-fA-F]{6}$/.test(v)) setSelected(v); }}
              placeholder="#3A8C8C" className="h-10 w-28 rounded-lg border border-[#D4D0C8] text-sm px-2 bg-white outline-none focus:border-[#3A8C8C] font-mono"
              data-testid="palette-hex-input" />
          </div>
        </div>
        <button onClick={save} disabled={saving || !isValid} data-testid="appearance-save"
          className="h-10 px-5 rounded-full bg-[#3A8C8C] text-white text-sm font-medium hover:bg-[#2E7A7A] disabled:opacity-50 flex items-center gap-2">
          <Save size={15} /> {saving ? 'Applying…' : 'Apply for everyone'}
        </button>
      </div>

      <div className="rounded-xl border border-[#E8E6E1] bg-[#FAFAF8] p-3 text-xs text-[#6B7280]">
        <span className="font-medium text-[#2C2C2E]">Preview:</span> buttons and highlights use{' '}
        <span className="inline-block align-middle w-3 h-3 rounded-full mx-1" style={{ background: isValid ? selected : '#3A8C8C' }} />
        <span className="font-mono">{selected}</span>.
        {hexToRgb(selected) ? '' : ' (invalid)'}
      </div>

      <div className="rounded-xl border border-[#E8E6E1] p-3 text-xs text-[#6B7280]">
        <span className="font-medium text-[#2C2C2E]">Dark mode</span> is coming soon — it needs a design-token pass so text stays readable on every surface. The color palette above works fully today.
      </div>
    </div>
  );
}

function TelegramCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);

  const load = async () => {
    try { const res = await api.get('/telegram/status'); setStatus(res.data); }
    catch { /* non-admin */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const register = async () => {
    setRegistering(true);
    try {
      const res = await api.post('/telegram/register-webhook');
      if (res.data.success) { toast.success('Webhook registered'); load(); }
      else toast.error('Registration failed — check token/URL');
    } catch (e) { toast.error(e.response?.data?.detail || 'Registration failed'); }
    finally { setRegistering(false); }
  };

  if (loading || !status) return null;
  const reachable = status.configured && status.webhook_info?.url;

  return (
    <div className="glass-card rounded-2xl p-6" data-testid="telegram-card">
      <div className="flex items-center gap-2 mb-4">
        <Send size={16} strokeWidth={1.5} className="text-[#3A8C8C]" />
        <h3 className="section-heading text-sm font-semibold font-['Manrope']">Telegram Bot</h3>
        <Badge className={status.configured ? 'bg-[#7DA68A]/10 text-[#7DA68A] text-[10px]' : 'bg-[#C67D5B]/10 text-[#C67D5B] text-[10px]'} data-testid="telegram-bot-status">
          {status.configured ? 'Bot token set' : 'Token not set'}
        </Badge>
        <Badge className={reachable ? 'bg-[#7DA68A]/10 text-[#7DA68A] text-[10px]' : 'bg-[#E8E6E1] text-[#6B7280] text-[10px]'}>
          {reachable ? 'Webhook active' : 'Webhook not registered'}
        </Badge>
      </div>
      <div className="space-y-2 text-xs text-[#6B7280]">
        <p>Field staff submit data via guided buttons in Telegram. {status.linked_count} member(s) linked.</p>
        <p className="break-all"><span className="text-[#2C2C2E] font-medium">Webhook:</span> {status.webhook_url || '—'}</p>
        {!status.configured && <p className="text-[#C67D5B]">Add <code>TELEGRAM_BOT_TOKEN</code> to the backend environment, then register.</p>}
        {status.webhook_info?.last_error_message && <p className="text-[#C4727F]">Last error: {status.webhook_info.last_error_message}</p>}
      </div>
      <button onClick={register} disabled={registering || !status.configured} data-testid="telegram-register-btn"
        className="mt-4 bg-[#3A8C8C] text-white rounded-full px-4 py-2 text-xs font-medium hover:bg-[#2E7A7A] transition-colors flex items-center gap-2 disabled:opacity-50">
        <RefreshCw size={13} strokeWidth={1.5} className={registering ? 'animate-spin' : ''} /> {registering ? 'Registering…' : 'Register / re-register webhook'}
      </button>
      <p className="mt-2 text-[10px] text-[#9CA3AF]">Re-run this after moving servers (when the public URL changes).</p>
    </div>
  );
}
